import {useCallback, useRef, useState} from 'react';
import {Pencil} from 'lucide-react';
import {ICON_SIZE} from 'components/common/constants';
import {SHARED_TOOLTIP_ID} from 'components/tooltip/AppTooltip.component';
import {Tooltip} from 'components/tooltip/Tooltip';
import * as Api from 'utils/api/api';
import type {MyTask, Translate} from 'types/myTasks';

const MAX_LENGTH = 250;

function crop(text: string) {
    return text.length > MAX_LENGTH ? text.slice(0, MAX_LENGTH) + '...' : text;
}

interface TaskCommentsCellProps {
    task: MyTask;
    translate: Translate;
}

/**
 * Comments badge with the classic list's hover tooltip: the last comment (and the saved draft,
 * when there is one) are fetched on hover rather than with the task list, which would mean two
 * extra requests per row.
 */
export const TaskCommentsCell = ({task, translate}: TaskCommentsCellProps) => {
    const [lastComment, setLastComment] = useState('');
    const [draftComment, setDraftComment] = useState('');
    const requested = useRef(false);

    const loadComments = useCallback(() => {
        if (requested.current) return;
        requested.current = true;

        Api.getLastCommentByUID(task.uid).then((response: any) => {
            if (response?.content) {
                setLastComment(response.userName + '\n' + response.content);
            }
        });

        if (task.hasDraftComment) {
            Api.getDraftComment(task.documentUid).then((response: any) => {
                if (response?.text) {
                    setDraftComment(response.text);
                }
            });
        }
    }, [task.uid, task.documentUid, task.hasDraftComment]);

    const tooltipContent = useCallback(() => {
        let result = '';
        if (lastComment) {
            result += translate('myTasks.task.lastComment') + crop(lastComment) + '\n\n';
        }
        if (draftComment) {
            result += translate('myTasks.task.savedDraft') + '\n' + crop(draftComment);
        }
        return result;
    }, [lastComment, draftComment, translate]);

    const tooltipId = 'task-comments-tooltip-' + task.id;

    return (
        <span className="inline-flex items-center" onMouseEnter={loadComments}
              data-tip={tooltipId} data-for={SHARED_TOOLTIP_ID}>
            <Tooltip id={tooltipId} content={tooltipContent}/>
            <span
                className="ga-badge ga-badge--text ga-badge--default d-inline-flex align-items-center justify-content-center"
                data-cy="myTasks.task.comment">
                {task.numberOfComments && task.numberOfComments > 0 ? task.numberOfComments : ''}
                {task.hasDraftComment &&
                    <Pencil size={ICON_SIZE.SMALL} className={task.numberOfComments ? 'ms-1' : ''}/>}
            </span>
        </span>
    );
};
